import React from 'react';
import { forms } from '../../styles/design-tokens';

/**
 * UnifiedInput Component
 * Standard form input component with consistent styling
 * 
 * @param {string} label - Input label text
 * @param {string} error - Error message
 * @param {string} helper - Helper text
 * @param {boolean} required - Required field indicator
 * @param {boolean} disabled - Disabled state
 * @param {string} className - Additional CSS classes
 * @param {string} type - Input type (text, email, password, etc.)
 */
const UnifiedInput = React.forwardRef(({ 
  label = '',
  error = '',
  helper = '',
  required = false,
  disabled = false,
  className = '',
  type = 'text',
  id = '',
  ...props 
}, ref) => {
  // Generate unique ID if not provided
  const inputId = id || `input-${Math.random()?.toString(36)?.substr(2, 9)}`;
  
  // Determine input class based on state
  const inputClass = error 
    ? `${forms?.input?.base} ${forms?.input?.error}` 
    : disabled 
    ? `${forms?.input?.base} ${forms?.input?.disabled}`
    : forms?.input?.base;
  
  return (
    <div className={`w-full ${className}`?.trim()}>
      {label && (
        <label htmlFor={inputId} className={forms?.label}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        type={type}
        disabled={disabled}
        className={inputClass}
        {...props}
      />
      {error && (
        <p className={forms?.error}>{error}</p>
      )}
      {helper && !error && (
        <p className={forms?.helper}>{helper}</p>
      )}
    </div>
  );
});

UnifiedInput.displayName = 'UnifiedInput';

export default UnifiedInput;